/**
 * The catalog: id-indexed categories, foods, recipes, availability and regions, plus the tree
 * walks everything else leans on. The engine never touches storage; callers build a catalog
 * from plain arrays and hand it in.
 */
import type {
  Aisle,
  AvailabilityRating,
  AvailabilityTable,
  Catalog,
  Category,
  Food,
  Match,
  Recipe,
  Region,
  Role,
  Warning,
} from './types'
import { timeOf } from './time'

export interface CatalogInput {
  categories?: readonly Category[]
  foods?: readonly Food[]
  recipes?: readonly Recipe[]
  availability?: AvailabilityTable
  regions?: readonly Region[]
}

/** Build a catalog. Soft-deleted foods and recipes are left out; later duplicates win. */
export function buildCatalog(input: CatalogInput): Catalog {
  const categories = new Map<string, Category>()
  for (const c of input.categories ?? []) categories.set(c.id, c)
  const foods = new Map<string, Food>()
  for (const f of input.foods ?? []) {
    if (f.reset) continue
    if (f.deleted) foods.delete(f.id)
    else foods.set(f.id, f)
  }
  const recipes = new Map<string, Recipe>()
  for (const r of input.recipes ?? []) {
    if (r.reset) continue
    if (r.deleted) recipes.delete(r.id)
    else recipes.set(r.id, r)
  }
  const availability: AvailabilityTable = {}
  for (const [cat, byRegion] of Object.entries(input.availability ?? {})) {
    availability[cat] = { ...byRegion }
  }
  const regions = new Map<string, Region>()
  for (const r of input.regions ?? []) regions.set(r.code, r)
  return { categories, foods, recipes, availability, regions }
}

const SOURCE_RANK: Record<AvailabilityRating['source'], number> = {
  curated: 0,
  community: 1,
  user: 2,
}

/** user > community > curated; within a source, the newer rating. */
export function betterRating(a: AvailabilityRating, b: AvailabilityRating): AvailabilityRating {
  const ra = SOURCE_RANK[a.source] ?? 0
  const rb = SOURCE_RANK[b.source] ?? 0
  if (ra !== rb) return ra > rb ? a : b
  return timeOf(b.updated_at) > timeOf(a.updated_at) ? b : a
}

function newer<T extends { updated_at?: string }>(base: T | undefined, overlay: T): boolean {
  return base === undefined || timeOf(overlay.updated_at) >= timeOf(base.updated_at)
}

/**
 * Lay user (or synced) records over the shipped catalog. A record replaces the shipped one
 * when its `updated_at` is the same or newer; a newer soft delete removes it; a reset marker
 * (`reset: true`) is no overlay at all, so the shipped record stays. Availability
 * cells resolve by source (user > community > curated), then recency.
 */
export function mergeCatalog(base: Catalog, overlay: CatalogInput): Catalog {
  const categories = new Map(base.categories)
  for (const c of overlay.categories ?? []) categories.set(c.id, c)

  const foods = new Map(base.foods)
  for (const f of overlay.foods ?? []) {
    if (f.reset) continue
    if (!newer(foods.get(f.id), f)) continue
    if (f.deleted) foods.delete(f.id)
    else foods.set(f.id, f)
  }
  const recipes = new Map(base.recipes)
  for (const r of overlay.recipes ?? []) {
    if (r.reset) continue
    if (!newer(recipes.get(r.id), r)) continue
    if (r.deleted) recipes.delete(r.id)
    else recipes.set(r.id, r)
  }

  const availability: AvailabilityTable = {}
  for (const [cat, byRegion] of Object.entries(base.availability)) {
    availability[cat] = { ...byRegion }
  }
  for (const [cat, byRegion] of Object.entries(overlay.availability ?? {})) {
    const cell = (availability[cat] ??= {})
    for (const [region, rating] of Object.entries(byRegion)) {
      const existing = cell[region]
      cell[region] = existing ? betterRating(existing, rating) : rating
    }
  }

  const regions = new Map(base.regions)
  for (const r of overlay.regions ?? []) regions.set(r.code, r)
  return { categories, foods, recipes, availability, regions }
}

// ---------------------------------------------------------------------------
// Tree walks
// ---------------------------------------------------------------------------

// Children index per category map, computed once. Pure: derived only from the map itself.
const childIndex = new WeakMap<Map<string, Category>, Map<string, string[]>>()

/** Child ids of a category, sorted by id so every walk is deterministic. */
export function childrenOf(cat: Catalog, id: string): string[] {
  let index = childIndex.get(cat.categories)
  if (!index) {
    index = new Map()
    for (const c of cat.categories.values()) {
      if (c.parent === null || c.parent === undefined) continue
      const list = index.get(c.parent) ?? []
      list.push(c.id)
      index.set(c.parent, list)
    }
    for (const list of index.values()) list.sort()
    childIndex.set(cat.categories, index)
  }
  return index.get(id) ?? []
}

/** Parent first, root last; excludes `id` itself. Cycle-safe. */
export function ancestors(cat: Catalog, id: string): string[] {
  const out: string[] = []
  const seen = new Set([id])
  let parent = cat.categories.get(id)?.parent
  while (parent && !seen.has(parent)) {
    out.push(parent)
    seen.add(parent)
    parent = cat.categories.get(parent)?.parent
  }
  return out
}

/** Every category below `id`, breadth-first (children by id); excludes `id` itself. */
export function descendants(cat: Catalog, id: string): string[] {
  const out: string[] = []
  const seen = new Set([id])
  const queue = [id]
  while (queue.length) {
    const next = queue.shift() as string
    for (const child of childrenOf(cat, next)) {
      if (seen.has(child)) continue
      seen.add(child)
      out.push(child)
      queue.push(child)
    }
  }
  return out
}

/** True when `child` is `ancestor` or anywhere below it. Inclusive on purpose: "any noodle". */
export function isUnder(cat: Catalog, child: string, ancestor: string): boolean {
  return child === ancestor || ancestors(cat, child).includes(ancestor)
}

/** 0 for a root category, 1 for its children, ... */
export function depthOf(cat: Catalog, id: string): number {
  return ancestors(cat, id).length
}

function liveGeneric(cat: Catalog, categoryId: string): Food | undefined {
  const id = cat.categories.get(categoryId)?.generic
  if (!id) return undefined
  const food = cat.foods.get(id)
  return food && !food.deleted ? food : undefined
}

/**
 * The best-guess food for "any of this category": its own generic, else the first descendant
 * (breadth-first) that has one, else the nearest ancestor that has one.
 */
export function genericFor(cat: Catalog, categoryId: string): Food | undefined {
  const own = liveGeneric(cat, categoryId)
  if (own) return own
  for (const d of descendants(cat, categoryId)) {
    const g = liveGeneric(cat, d)
    if (g) return g
  }
  for (const a of ancestors(cat, categoryId)) {
    const g = liveGeneric(cat, a)
    if (g) return g
  }
  return undefined
}

function inherited<T>(cat: Catalog, id: string, pick: (c: Category) => T | undefined) {
  for (const cid of [id, ...ancestors(cat, id)]) {
    const c = cat.categories.get(cid)
    if (!c) continue
    const v = pick(c)
    if (v !== undefined) return v
  }
  return undefined
}

export function aisleOf(cat: Catalog, categoryId: string): Aisle {
  return inherited(cat, categoryId, (c) => c.aisle) ?? 'other'
}

export function rolesOf(cat: Catalog, categoryId: string): Role[] {
  return inherited(cat, categoryId, (c) => (c.roles?.length ? c.roles : undefined)) ?? []
}

export function isNonCaloric(cat: Catalog, categoryId: string): boolean {
  return inherited(cat, categoryId, (c) => c.non_caloric) ?? false
}

/** The category a match lives in: the category itself, or the food's category. */
export function categoryOfMatch(cat: Catalog, match: Match): string | undefined {
  if ('food' in match) return cat.foods.get(match.food)?.category
  return match.category
}

export function matchLabel(cat: Catalog, match: Match): string {
  if ('food' in match) return cat.foods.get(match.food)?.name ?? match.food
  return cat.categories.get(match.category)?.name ?? match.category
}

export function matchId(match: Match): string {
  return 'food' in match ? `f:${match.food}` : `c:${match.category}`
}

/** Canonical shopping-line key: "f:<id>" / "c:<id>", de-duplicated, sorted, joined by "|". */
export function matchKey(matches: readonly Match[]): string {
  return [...new Set(matches.map(matchId))].sort().join('|')
}

/** The inverse of `matchKey`. Unknown prefixes are dropped. */
export function matchesFromKey(key: string): Match[] {
  const out: Match[] = []
  for (const part of key.split('|')) {
    if (part.startsWith('f:')) out.push({ food: part.slice(2) })
    else if (part.startsWith('c:')) out.push({ category: part.slice(2) })
  }
  return out
}

// ---------------------------------------------------------------------------
// Integrity
// ---------------------------------------------------------------------------

/** Tree and reference integrity. Errors break the engine's assumptions; warns are smells. */
export function validateCatalog(cat: Catalog): Warning[] {
  const out: Warning[] = []
  const err = (code: string, message: string) => out.push({ level: 'error', code, message })
  const warn = (code: string, message: string) => out.push({ level: 'warn', code, message })

  for (const c of cat.categories.values()) {
    if (c.parent && !cat.categories.has(c.parent)) {
      err('unknown-parent', `Category ${c.id} has unknown parent ${c.parent}`)
    }
    // A cycle shows up as the walk returning to itself.
    const seen = new Set<string>()
    let p = c.parent
    while (p) {
      if (p === c.id) {
        err('category-cycle', `Category ${c.id} is its own ancestor`)
        break
      }
      if (seen.has(p)) break
      seen.add(p)
      p = cat.categories.get(p)?.parent ?? null
    }
    if (c.generic) {
      const g = cat.foods.get(c.generic)
      if (!g) err('unknown-generic', `Category ${c.id} names unknown generic ${c.generic}`)
      else if (!isUnder(cat, g.category, c.id) && !isUnder(cat, c.id, g.category)) {
        warn(
          'generic-outside',
          `Generic ${g.id} for ${c.id} is in unrelated category ${g.category}`,
        )
      }
    }
  }

  for (const f of cat.foods.values()) {
    if (!cat.categories.has(f.category)) {
      err('unknown-category', `Food ${f.id} has unknown category ${f.category}`)
    }
    if (!Number.isFinite(f.kcal_per_100g) || f.kcal_per_100g < 0) {
      err('bad-energy', `Food ${f.id} has invalid kcal_per_100g`)
    } else if (f.kcal_per_100g > 900) {
      warn('implausible-energy', `Food ${f.id} has more than 9 kcal/g`)
    }
    if (f.pack && !(f.pack.size_g > 0)) warn('bad-pack', `Food ${f.id} has a pack with no size`)
  }

  for (const r of cat.recipes.values()) {
    if (!r.ingredients.length) warn('empty-recipe', `Recipe ${r.id} has no ingredients`)
    for (const ing of r.ingredients) {
      if (!ing.options.length) {
        err('no-options', `Recipe ${r.id} ingredient ${ing.id} has no options`)
      }
      if (ing.default !== undefined && (ing.default < 0 || ing.default >= ing.options.length)) {
        warn('bad-default', `Recipe ${r.id} ingredient ${ing.id} default is out of range`)
      }
      for (const m of ing.options) {
        const known =
          'food' in m
            ? cat.foods.has(m.food) || cat.recipes.has(m.food)
            : cat.categories.has(m.category)
        if (!known) err('unknown-option', `Recipe ${r.id} ingredient ${ing.id}: ${matchId(m)}`)
        else if ('category' in m && !genericFor(cat, m.category)) {
          warn('no-generic', `No generic food for category ${m.category} (recipe ${r.id})`)
        }
      }
    }
  }

  for (const id of Object.keys(cat.availability)) {
    if (!cat.categories.has(id)) warn('unknown-availability', `Availability for unknown ${id}`)
  }
  return out
}
